// Which pending edits a finished job has settled, and may be cleared.
//
// ⚠ NOT SIMPLY `writeStatus === 'written'`, which is what this used to be.
// A resumed job — or one whose record already held the target value — comes
// back as `skipped`, because there was nothing left to write. Acumatica HAS the
// value the user asked for, but the edit stayed in the map: the grid went on
// showing it as pending, and confirming again sent another job of no-ops that
// spent the customer's API budget and read as though the first job had failed.
//
// ⚠ THE DISCRIMINATOR IS THE OBSERVED VALUE, NOT THE MESSAGE. `markChangeResult`
// stores what Acumatica actually held immediately before the write into
// `beforeValue`, so a settled change is one where the record now reads what the
// edit asked for. Matching on the errorReason prose would work until somebody
// reworded it, and the two skips differ only in their wording.
//
// The other skip must NOT clear: a conflict means a third party changed the
// field, we refused to clobber it, and the user's edit is still outstanding and
// still theirs to decide about. Same for an outright failure.

export interface SettledChange {
  recordId: string;
  fieldName: string;
  beforeValue: string | null;
  afterValue: string | null;
  writeStatus: 'pending' | 'written' | 'failed' | 'skipped';
}

/** Acumatica's empty and ours have to compare equal. */
function normalise(value: string | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

/**
 * True when the record now holds what this edit asked for, so keeping the edit
 * would be showing the user work that is already done.
 */
export function isSettled(change: SettledChange): boolean {
  if (change.writeStatus === 'written') return true;

  // A skip that reached the target anyway: our own interrupted write, or a
  // value that was already correct.
  if (change.writeStatus === 'skipped') {
    return normalise(change.beforeValue) === normalise(change.afterValue);
  }

  // 'failed' and anything still 'pending' stay in the grid.
  return false;
}

/** Keys of the edits a finished job has settled, as `recordId:fieldName`. */
export function settledEditKeys(changes: SettledChange[]): Set<string> {
  return new Set(
    changes.filter(isSettled).map((c) => `${c.recordId}:${c.fieldName}`)
  );
}
